/**
 * Módulo Portfólio: visão executiva multiprojeto, lista de projetos, capacidade
 * da equipe, simulador de projeto e os painéis de análise/comparação/risco.
 *
 * Os textos foram RECORTADOS dos componentes sem reescrita: se a tela dizia
 * "Esforco" sem acento (PortfolioAnalysis) e "Esforço" com acento (Overview), o
 * dicionário repete os dois. Corrigir redação aqui seria mudar produto no meio
 * de uma tradução.
 *
 * Onde o componente tinha um array/objeto de nível de módulo com `{ value,
 * label }`, só o rótulo veio para cá: o `value`/`id` continua no componente
 * porque é ele que o `switch`, o `filter` e o banco enxergam.
 */
const portfolio = {
  /** Casca do módulo: cabeçalho e as três abas. */
  module: {
    export: 'Exportar',
    exporting: 'Exportando...',
    exportPdf: 'Exportar PDF',
    exportImage: 'Exportar Imagem',
    exportExcel: 'Exportar Excel',
    title: 'Portfólio de Projetos',
    loading: 'Carregando portfólio...',
    simulate: 'Simular Projeto',
    simulateHint: 'Simular novo projeto',
    tabs: {
      overview: 'Visão Geral',
      projects: 'Projetos',
      team: 'Equipe',
    },
    /** Recorte da carteira por pasta (cliente). Vale para as três abas. */
    folderFilter: {
      all: 'Toda a carteira',
    },
  },

  /** Aba Visão Geral: KPIs, EVM, curva S, capacidade, roadmap e gráficos. */
  overview: {
    filter: {
      label: 'Projeto:',
      all: 'Todos os projetos',
      singleMode: 'Modo projeto único',
      clear: 'Limpar filtro',
    },

    kpis: {
      projects: 'Projetos',
      avgProgress: 'Progresso Médio',
      /** `{{count}}` é o número de recursos. */
      utilization: 'Utilização · {{count}} recursos',
      budget: 'Orçamento Total',
      overdue: 'Tarefas Atrasadas',
      overbooking: 'Overbooking',
    },

    /**
     * Cards EVM. As siglas (PV, EV, AC, SPI, CPI, BAC, EAC) não se traduzem:
     * são nomenclatura PMBoK, iguais em qualquer idioma.
     */
    evm: {
      title: 'Earned Value Management',
      subtitle: 'PMBoK · baseline (PV) vs valor agregado (EV) vs custo realizado (AC)',
      aboutAria: 'Sobre EVM',
      /** Frase do tooltip do "i", partida porque "3 grandezas" vai em negrito. */
      aboutPre: 'EVM (PMBoK 7ª ed.) compara ',
      aboutStrong: '3 grandezas',
      aboutPost: ': PV (planejado), EV (entregue) e AC (real) — pra dizer se o projeto está no prazo, no orçamento e quanto vai custar no fim.',
      onSchedule: 'No prazo',
      scheduleWarning: 'Atenção prazo',
      late: 'Atrasado',
      onBudget: 'No orçamento',
      costWarning: 'Atenção custo',
      overBudget: 'Acima do orçamento',
      learnMore: 'Saiba mais',
      learnMoreAria: 'Saiba mais sobre EVM',

      /** Estado vazio: checklist dos 3 ingredientes que ligam o cálculo. */
      emptyPre: 'Pra ativar o EVM em pelo menos um projeto, configure os ',
      emptyStrong: '3 ingredientes',
      emptyPost: ' abaixo. Padrão PMBoK: sem dado de entrada, não há cálculo válido.',
      step1: '1. Taxa/hora dos recursos',
      step1Hint: 'Portfólio → Equipe → Gerenciar Recursos. Calculadora salário→taxa disponível.',
      step2: '2. Estimar horas das tarefas',
      step2Hint: 'Gantt → coluna "Estimado (h)" inline · ou Kanban → card → campo "Estimado".',
      step3: '3. Salvar baseline',
      step3Hint: 'Gantt → botão "Salvar Baseline". Snapshot imutável que vira PV.',
      emptyCta: 'Entenda EVM em detalhes →',

      pvTitle: 'Planned Value (PV)',
      pvHint: 'Baseline × taxa/hora',
      pvTooltip: 'PV: orçamento previsto até hoje conforme baseline',
      evTitle: 'Earned Value (EV)',
      evHint: 'Trabalho concluído · valor',
      evTooltip: 'EV: Σ(estimado × progresso × taxa) — valor "ganho" pelo trabalho feito',
      acTitle: 'Actual Cost (AC)',
      acHint: 'Horas reais × taxa/hora',
      acTooltip: 'AC: custo realmente gasto até agora',
      spiTooltip: 'Schedule Performance Index — <1 atrasado, >1 adiantado. SV = EV − PV. Sparkline = evolução semanal do SPI.',
      cpiTooltip: 'Cost Performance Index — <1 acima do orçamento. CV = EV − AC. Sparkline = evolução semanal do CPI.',
      bacHint: 'Orçamento total',
      bacTooltip: 'Budget at Completion: orçamento total aprovado (baseline)',
      eacHint: 'Estimativa total',
      eacTooltip: 'Estimate at Completion: projeção do custo final = BAC / CPI',
    },

    /** Curva S do EVM. */
    scurve: {
      title: 'Curva S — Earned Value',
      subtitle: 'Acumulado semanal · PV (planejado) × EV (agregado) × AC (real) · EAC = forecast (BAC ÷ CPI)',
      backfillBadge: 'Inclui histórico estimado',
      backfillTooltip1: 'Pontos retroativos foram reconstruídos por linearização (PMBoK Data Date estimada).',
      backfillTooltip2: 'Snapshots reais começam a ser capturados a cada segunda-feira pelo cron.',
      today: 'Hoje',
      legendPv: 'PV — Planned Value (baseline)',
      legendEv: 'EV — Earned Value (agregado)',
      legendAc: 'AC — Actual Cost (real)',
      legendEac: 'EAC — Estimate at Completion',
      configPv: 'PV (planejado)',
      configEv: 'EV (agregado)',
      configAc: 'AC (real)',
      configEac: 'EAC (forecast)',
    },

    capacity: {
      title: 'Capacidade da Equipe',
      subtitle: 'Próximas 8 semanas · utilização % por recurso (verde <70 · amarelo 70-90 · vermelho ≥90)',
    },

    attention: {
      title: 'Atenção Necessária',
      overbook: 'Overbook',
      critical: 'Crítico',
    },

    health: {
      title: 'Saúde dos Projetos',
      subtitle: 'Progresso, prazo, custo e risco',
    },

    burnup: {
      title: 'Burn-up de Esforço',
      /** `{{done}}`/`{{total}}` em horas, `{{pct}}` percentual do portfólio. */
      subtitle: '{{done}}h de {{total}}h · {{pct}}% do portfólio',
      done: 'Concluído: {{hours}}h',
      estimated: 'Estimado: {{hours}}h',
      actual: 'Real: {{hours}}h',
      overrun: 'Overrun: +{{hours}}h',
    },

    roadmap: {
      title: 'Roadmap',
      today: 'Hoje',
    },

    radar: {
      title: 'Comparativo',
      subtitle: 'Multi-dimensional',
      /** `{{count}}` = quantos projetos estão no radar. */
      picker: '{{count}} projetos',
      /**
       * Rótulos curtos dos eixos, indexados pelo `id` de RADAR_DIMENSIONS. A
       * chave é o discriminante do cálculo e nunca muda com o idioma.
       */
      dimensions: {
        progress: 'Progresso',
        schedule: 'Prazo',
        cost: 'Custo',
        risk: 'Risco',
        effort: 'Esforço',
      },
    },

    riskMap: {
      title: 'Mapa de Riscos',
      subtitle: 'Probabilidade × Impacto',
      /** Ordem baixa → muito alta; casa por índice com HEATMAP_KEYS. */
      probs: ['Baixa', 'Média', 'Alta', 'Muito Alta'],
      impacts: ['Baixo', 'Médio', 'Alto', 'Crítico'],
    },

    effort: {
      title: 'Esforço por Projeto',
      subtitle: 'Dias de trabalho estimados',
      configLabel: 'Esforço (dias)',
    },

    cost: {
      title: 'Custo por Projeto',
      subtitle: 'Planejado vs realizado',
      configPlanned: 'Planejado',
      configActual: 'Realizado',
    },

    risky: {
      title: 'Projetos em Risco',
      /** `{{count}}` tarefas atrasadas / riscos altos. Badge curto. */
      overdue: '{{count}} atraso',
      risks: '{{count}} risco',
    },

    statusDist: {
      title: 'Status dos Projetos',
      /** `{{count}}` projetos; sufixo muda se há filtro de projeto único. */
      subtitleSelected: '{{count}} {{noun}} selecionado',
      subtitleAll: '{{count}} {{noun}} no portfólio',
      nounOne: 'projeto',
      nounOther: 'projetos',
    },

    status: {
      active: 'Ativo',
      planning: 'Planejamento',
      paused: 'Pausado',
      completed: 'Concluído',
      canceled: 'Cancelado',
    },
  },

  /** Aba Projetos: filtros, grade e tabela. */
  projectList: {
    empty: 'Nenhum projeto encontrado',
    emptyHint: 'Crie projetos para visualizar a lista',
    searchPlaceholder: 'Buscar projeto...',
    allStatus: 'Todos',
    allPriority: 'Todas',
    /** Prefixo do seletor de ordenação: "Ordenar: Nome". */
    sortPrefix: 'Ordenar: {{label}}',
    viewGrid: 'Grade',
    viewList: 'Lista',
    noMatch: 'Nenhum projeto corresponde aos filtros',
    noMatchHint: 'Tente ajustar os filtros de busca',
    statusError: 'Erro ao atualizar status',
    priorityError: 'Erro ao atualizar prioridade',

    status: {
      planning: 'Planejamento',
      active: 'Ativo',
      paused: 'Pausado',
      completed: 'Concluído',
      canceled: 'Cancelado',
    },
    priority: {
      critical: 'Crítica',
      high: 'Alta',
      medium: 'Média',
      low: 'Baixa',
    },
    sort: {
      name: 'Nome',
      progress: 'Progresso',
      overdue: 'Atrasadas',
      cost: 'Custo',
      risk: 'Risco',
      updated: 'Atualização',
    },

    columns: {
      name: 'Nome',
      status: 'Status',
      priority: 'Prioridade',
      progress: 'Progresso',
      tasks: 'Tarefas',
      budget: 'Orçamento',
      risks: 'Riscos',
      period: 'Período',
    },
    /** `{{count}}` tarefas atrasadas, entre parênteses na célula de tarefas. */
    overdueCell: '({{count}} atrasadas)',
    openRisksOne: '{{count}} aberto',
    openRisksOther: '{{count}} abertos',
  },

  /**
   * Painel de Análise. O português aqui está sem acento em vários rótulos
   * ("Esforco", "Saude", "Media") porque é assim que a tela sempre mostrou.
   */
  analysis: {
    empty: 'Nenhum projeto encontrado',
    emptyHint: 'Crie projetos para visualizar a analise',

    radar: {
      title: 'Radar Multi-Dimensional',
      subtitle: 'Comparativo em 5 eixos',
      noData: 'Sem dados',
      /** `{{count}}` de no máximo 5 projetos selecionados. */
      select: 'Selecionar Projetos ({{count}}/5)',
      dimensions: {
        progress: 'Progresso',
        schedule: 'Prazo',
        cost: 'Eficiência Custo',
        risk: 'Baixo Risco',
        effort: 'Esforço',
      },
    },

    heatmap: {
      title: 'Heatmap de Riscos',
      subtitle: 'Probabilidade x Impacto',
      empty: 'Nenhum risco cadastrado',
      probability: 'Probabilidade',
      impact: 'Impacto',
      /** Title do quadrante: "Alta x Critico: 3 riscos". */
      cellTitle: '{{prob}} x {{impact}}: {{count}} riscos',
      less: 'Menor',
      more: 'Maior',
      probs: {
        low: 'Baixa',
        medium: 'Media',
        high: 'Alta',
        very_high: 'Muito Alta',
      },
      impacts: {
        low: 'Baixo',
        medium: 'Medio',
        high: 'Alto',
        critical: 'Critico',
      },
    },

    effort: {
      title: 'Esforco por Projeto',
      subtitle: 'Dias estimados de trabalho',
      empty: 'Nenhum projeto com tarefas',
      configLabel: 'Esforco (dias)',
      /** Linha de referência da média, em dias. */
      average: 'Media: {{value}}d',
    },

    cost: {
      title: 'Custo por Projeto',
      subtitle: 'Planejado vs Realizado',
      empty: 'Nenhum custo cadastrado',
      configPlanned: 'Planejado',
      configActual: 'Realizado',
      average: 'Media: {{value}}',
    },

    timeline: {
      title: 'Timeline dos Projetos',
      subtitle: 'Cronograma com progresso',
      empty: 'Nenhum projeto com datas definidas',
      today: 'Hoje',
    },

    bottlenecks: {
      title: 'Gargalos',
      subtitle: 'Projetos com pontos de atencao',
      empty: 'Nenhum gargalo identificado',
      colProject: 'Projeto',
      colHealth: 'Saude',
      colOverdue: 'Atrasadas',
      colHighRisks: 'Riscos Altos',
      colVariance: 'Variacao Orcamento',
    },

    health: {
      green: 'Saudavel',
      yellow: 'Atencao',
      red: 'Critico',
    },

    status: {
      planning: 'Planejamento',
      active: 'Ativo',
      paused: 'Pausado',
      completed: 'Concluido',
      canceled: 'Cancelado',
    },
  },

  /** Painel de Comparação. */
  comparison: {
    empty: 'Nenhum projeto encontrado',
    emptyHint: 'Crie projetos para visualizar comparativos',

    radar: {
      title: 'Comparativo Multi-Dimensional',
      select: 'Selecionar Projetos ({{count}}/5)',
      hint: 'Área maior em Progresso e Prazo = melhor. Valores altos em Esforço indicam maior consumo de recursos.',
      dimensions: {
        progress: 'Progresso',
        schedule: 'Prazo',
        cost: 'Eficiência Custo',
        risk: 'Baixo Risco',
        effort: 'Esforço',
      },
    },

    health: {
      title: 'Saúde dos Projetos',
      /** `{{pct}}` = progresso do projeto. */
      progress: '{{pct}}% progresso',
      good: 'Saudável',
      warning: 'Atenção',
      critical: 'Crítico',
    },

    effort: {
      title: 'Esforço por Projeto (dias)',
      empty: 'Nenhum projeto com tarefas cadastradas',
      configLabel: 'Esforço (dias)',
      average: 'Média: {{value}}d',
    },

    cost: {
      title: 'Custo por Projeto',
      empty: 'Nenhum projeto com custos cadastrados',
      configPlanned: 'Planejado',
      configActual: 'Realizado',
      average: 'Média: {{value}}',
    },

    timeline: {
      title: 'Timeline dos Projetos',
      empty: 'Nenhum projeto com datas definidas',
      today: 'Hoje',
    },

    status: {
      planning: 'Planejamento',
      active: 'Ativo',
      paused: 'Pausado',
      completed: 'Concluído',
      canceled: 'Cancelado',
    },
  },

  /** Painel de Análise de Riscos: mapa de calor e tabela de gargalos. */
  riskAnalysis: {
    heatmapTitle: 'Mapa de Calor de Riscos',
    empty: 'Nenhum risco cadastrado',
    emptyHint: 'Adicione riscos aos seus projetos para visualizar o mapa de calor',
    probability: 'Probabilidade',
    impact: 'Impacto',
    cellTitle: '{{prob}} x {{impact}}: {{count}} riscos',
    less: 'Menor risco',
    more: 'Maior risco',

    probs: {
      low: 'Baixa',
      medium: 'Média',
      high: 'Alta',
      very_high: 'Muito Alta',
    },
    impacts: {
      low: 'Baixo',
      medium: 'Médio',
      high: 'Alto',
      critical: 'Crítico',
    },

    bottlenecksTitle: 'Gargalos e Pontos de Atenção',
    noBottlenecks: 'Nenhum gargalo identificado',
    noBottlenecksHint: 'Todos os projetos estão saudáveis',
    colHealth: 'Saúde',
    colProject: 'Projeto',
    colOverdue: 'Atrasadas',
    colCriticalRisks: 'Riscos Críticos',
    colVariance: 'Variação Orçamento',

    health: {
      green: 'Saudável',
      yellow: 'Atenção',
      red: 'Crítico',
    },
  },

  /** Roadmap do portfólio, agrupado por status. */
  roadmap: {
    title: 'Roadmap do Portfólio',
    empty: 'Nenhum projeto com datas',
    emptyHint: 'Defina datas nas tarefas dos seus projetos para visualizar o roadmap',
    /** Prefixo do trimestre na régua ("T1/26"). */
    quarterPrefix: 'T',
    progress: 'Progresso: {{pct}}%',
    effort: 'Esforço: {{days}} dias',

    scales: {
      month: 'Mês',
      quarter: 'Trimestre',
      year: 'Ano',
    },
    status: {
      active: 'Ativos',
      planning: 'Planejamento',
      paused: 'Pausados',
      completed: 'Concluídos',
      canceled: 'Cancelados',
    },
  },

  /** Aba Equipe: hub de capacidade e os cards que vivem nele. */
  capacity: {
    loading: 'Carregando dados de capacidade...',
    emptyTitle: 'Adicione membros aos seus projetos',
    emptyBody: 'Ao adicionar membros na aba Equipe de cada projeto, a capacidade é criada automaticamente. Você também pode criar recursos manualmente para planejamento avançado.',
    manageResources: 'Gerenciar Recursos',
    firstVisit: "Aqui você vê a capacidade da equipe. Barra 'Alocado' = horas planejadas. Barra 'Tarefas' = carga real baseada nas tarefas atribuídas.",

    /**
     * Fila de conflito: o miolo da aba Equipe. Lista as pessoas cuja alocação
     * estoura o limite dentro da janela, somando o que cada projeto reservou, e
     * resolve ali mesmo. Só entra nesta fila quem tem conflito: o agregado da
     * equipe inteira é opt-in, pelo botão "Ver todas as pessoas".
     */
    queue: {
      title: 'Equipe do Portfólio',
      subtitle: 'Conflitos de alocação de pessoas entre projetos, semana a semana',

      /** Filtros da barra. `{{weeks}}` é 4, 8 ou 12. */
      window: 'Próximas {{weeks}} semanas',
      roleAll: 'Todos os papéis',

      /**
       * Manchete: uma frase montada por pedaços, e cada pedaço passa por
       * `fmt.plural`. Por isso vêm em `{ one, other }` com o número entrando
       * por `{{count}}`, e não como texto pronto.
       */
      headlineOver: {
        one: '{{count}} pessoa acima do limite',
        other: '{{count}} pessoas acima do limite',
      },
      headlineClear: 'Ninguém acima do limite',
      /**
       * Cabe no período mas estoura em alguma semana. É problema diferente de
       * "acima do limite" e pede solução diferente: aqui se remaneja, lá se tira.
       */
      headlineUneven: {
        one: '{{count}} mal distribuída',
        other: '{{count}} mal distribuídas',
      },
      headlineIdle: {
        one: '{{count}} ociosa',
        other: '{{count}} ociosas',
      },
      headlineAbsent: {
        one: '{{count}} ausente',
        other: '{{count}} ausentes',
      },
      headlineNoPlan: {
        one: '{{count}} sem plano',
        other: '{{count}} sem plano',
      },

      colPerson: 'Pessoa',
      colWhere: 'Projeto',
      colPeriod: 'Período',
      colLimit: 'Limite',
      colPlanned: 'Planejado',
      colExcess: 'Excesso',
      projectFallback: 'Projeto sem nome',

      /**
       * Estado de carga, chaveado pelo `CapacityState` do domínio. É vocabulário
       * FIXO de gestão de recursos, não sinônimo de escolha do redator:
       * "superalocado" é o overallocated do PMBoK, e "mal distribuído" é o caso
       * que cabe no período mas estoura em alguma semana (estado do Float). O
       * rótulo só aparece na célula de Excesso quando não há horas de estouro
       * para mostrar no lugar dele.
       */
      stateShort: {
        ocioso: 'Ocioso',
        saudavel: 'Saudável',
        no_limite: 'No limite',
        mal_distribuido: 'Mal distribuído',
        superalocado: 'Superalocado',
        sem_plano: 'Sem plano',
      },

      resolve: 'Ajustar horas',
      createAllocation: 'Criar alocação',
      viewAllPeople: 'Ver todas as pessoas',

      /**
       * Aviso honesto: sem conta no sistema não existe execução para confrontar
       * com o plano, então a linha mostra plano e para por aí.
       */
      noExecutionLink: 'Sem execução vinculada',
      noExecutionLinkHint: 'Esta pessoa não tem conta no MeuGantt, então o plano aparece aqui, mas não há execução para comparar.',

      emptyTitle: 'Nenhum conflito nesta janela',
      /** `{{weeks}}` = tamanho da janela escolhida no filtro. */
      emptyBody: 'Ninguém passa do limite nas próximas {{weeks}} semanas. Amplie a janela para enxergar mais longe.',

      /** Diálogo de conflito, quando a mudança não cabe no limite da pessoa. */
      dialogTitle: 'A mudança não cabe para {{name}}',
      dialogBody: 'Na semana de {{week}} o plano soma {{planned}}h para um limite de {{capacity}}h. Ajuste as horas por projeto e veja o resultado antes de salvar.',
      appliesTo: 'Vale de {{start}} a {{end}}',
      hoursPerWeek: 'h/sem',
      dialogFits: 'Fica em {{planned}}h de {{capacity}}h, com {{free}}h livres.',
      dialogStillOver: 'Ainda fica em {{planned}}h de {{capacity}}h, {{excess}}h acima do limite.',

      cancel: 'Cancelar',
      save: 'Salvar',
      saved: 'Plano atualizado',
      saveError: 'Erro ao salvar o plano',
    },

    periods: {
      week: 'Semana',
      month: 'Mês',
      quarter: 'Trimestre',
    },
    startPlaceholder: 'Início',
    rangeSeparator: 'até',
    endPlaceholder: 'Fim',
    viewList: 'Lista',
    viewHeatmap: 'Heat Map',
    filters: 'Filtros',

    resumeRealtime: 'Retomar atualizações em tempo real',
    pauseRealtime: 'Pausar atualizações em tempo real',
    paused: 'Pausado',
    live: 'Ao vivo',
    pausedHint: 'Dados congelados. Clique para retomar atualizações automáticas.',
    liveHint: 'Atualizando em tempo real quando o Kanban, Gantt ou alocações mudam.',
    /** Sufixo do tooltip "Ao vivo"; `{{time}}` já vem formatado por fmt.time. */
    lastSync: ' Última sync: {{time}}.',

    roleLabel: 'Cargo',
    allRoles: 'Todos os cargos',
    projectLabel: 'Projeto',
    allProjects: 'Todos os projetos',
    showInactive: 'Mostrar inativos',
    clearFilters: 'Limpar filtros',

    attentionTitle: 'Atenção Necessária',
    overbook: 'Overbook',
    critical: 'Crítico',

    errorCreate: 'Erro ao criar recurso',
    errorUpdate: 'Erro ao atualizar recurso',
    errorDelete: 'Erro ao excluir recurso',
    allocationUpdated: 'Alocação atualizada',
    errorAllocationUpdate: 'Erro ao atualizar alocação',
    allocationRemoved: 'Alocação removida',
    errorAllocationDelete: 'Erro ao remover alocação',

    /** Heat map recurso × semana. */
    heatmap: {
      empty: 'Sem dados para exibir no heat map',
      legend: 'Utilização:',
      resource: 'Recurso',
      unknownProject: 'Projeto',
      noAllocations: 'Sem alocações',
    },

    /** Card de um recurso na lista. */
    card: {
      inactive: 'Inativo',
      noRole: 'Sem cargo',
      /** `{{hours}}` = capacidade semanal nominal do recurso. */
      capacityTitle: 'Capacidade nominal do recurso: {{hours}}h/sem. Total no período reflete dias úteis do calendário dos projetos alocados.',
      capacity: 'Capacidade',
      planTitle: 'Horas planejadas (alocações manuais)',
      plan: 'Plano',
      execTitle: 'Horas derivadas das tarefas atribuídas (execução real)',
      exec: 'Execução',
      excess: 'Excesso',
      free: 'Livre',
      divergence: 'Divergência: {{value}}',
    },

    /**
     * Status conciliado do recurso. A chave é o `ReconciledStatus` do domínio,
     * calculado no service: só o texto de tela vive aqui.
     */
    reconciled: {
      'no-formal-plan': 'Sem plano formal',
      'idle-plan': 'Plano ocioso',
      'exec-over-plan': 'Execução acima do plano',
      overbooking: 'Overbooking',
      critical: 'Crítico',
      attention: 'Atenção',
      available: 'Disponível',
    },

    /** Detalhe expandido de um recurso. */
    detail: {
      unknownProject: 'Projeto desconhecido',
      allocations: 'Alocações',
      hoursPerWeek: 'h/sem',
      confirmRemove: 'Remover?',
      edit: 'Editar',
      remove: 'Remover',
      noAllocations: 'Nenhuma alocação no período selecionado',
      weeklyTitle: 'Ocupação por Semana',
      utilization: 'Utilização',
      /** `{{count}}` semanas além das 12 exibidas. */
      moreWeeks: '+ {{count}} semanas adicionais',
    },

    /** Card "Alocação Semanal". */
    teamOverview: {
      title: 'Alocação Semanal',
      subtitle: 'Horas alocadas por semana da equipe',
      configLabel: 'Alocado',
      average: 'Média {{pct}}%',
      overbookWeeks: ' · {{count}} sem. com overbooking',
    },

    /** Card "Disponibilidade". */
    forecast: {
      title: 'Disponibilidade',
      subtitle: 'Quando cada recurso ficará livre (<70%)',
      availableNow: 'Disponível',
      inWeeks: '{{count}} sem.',
      beyond: '+12 sem.',
      countNow: '{{count}} disponível(eis) agora',
      countSoon: '{{count}} em até 4 sem.',
      none: 'Nenhum recurso disponível em breve',
    },

    /** Card "Custo da Equipe". */
    burnRate: {
      title: 'Custo da Equipe',
      subtitle: 'Burn rate baseado em horas × taxa/hora',
      footer: 'Total: {{total}} · Média: {{avg}}/sem',
    },
  },

  /** Modal Gerenciar Recursos. */
  resources: {
    title: 'Gerenciar Recursos',
    /** `{{name}}` = nome do recurso que já usa o email. */
    duplicateEmail: 'Já existe um recurso com este email: {{name}}',
    saveError: 'Erro ao salvar recurso',

    orgPrefix: 'Org: ',
    unlinkedOne: ' — {{count}} membro sem recurso vinculado',
    unlinkedOther: ' — {{count}} membros sem recurso vinculado',
    syncing: 'Sincronizando...',
    sync: 'Sincronizar da Org',
    syncedOne: '{{count}} recurso sincronizado',
    syncedOther: '{{count}} recursos sincronizados',
    syncedNone: 'Todos os membros já estão sincronizados',
    syncError: 'Erro ao sincronizar',

    searchPlaceholder: 'Buscar recurso...',
    new: 'Novo Recurso',
    editTitle: 'Editar Recurso',

    linkLabel: 'Vincular a membro da organização',
    linkNone: 'Nenhum (recurso manual)',
    memberFallback: 'Membro',

    name: 'Nome *',
    namePlaceholder: 'Nome completo',
    email: 'Email',
    emailPlaceholder: 'email@exemplo.com',
    role: 'Cargo',
    rolePlaceholder: 'Ex: Dev Senior, UX Designer',
    weeklyHours: 'Horas/semana',
    /** (R$) fica: a cobrança do produto é em real, não muda com o idioma. */
    hourlyRate: 'Taxa/hora (R$)',
    calcClose: '− Fechar calculadora',
    calcOpen: '+ Calcular a partir de salário mensal',
    salaryMonthly: 'Salário mensal (R$)',
    salaryPlaceholder: 'ex: 8000',
    hoursPerMonth: 'Horas/mês',
    apply: 'Aplicar',
    rateHint: 'Defina taxa/hora para habilitar cálculo automático de custos no Portfolio.',
    skills: 'Skills (separadas por vírgula)',
    skillsPlaceholder: 'React, Node.js, UX, Gestão',
    avatarColor: 'Cor do avatar',
    active: 'Ativo',
    cancel: 'Cancelar',
    saving: 'Salvando...',
    save: 'Salvar',

    emptySearch: 'Nenhum recurso encontrado',
    emptyList: 'Nenhum recurso cadastrado',
    emptyHint: 'Clique em "Novo Recurso" para começar',
    linked: 'Vinculado',
    linkedTooltip: 'Usuário vinculado da organização',
    inactive: 'Inativo',
    hoursSuffix: 'h/sem',
    edit: 'Editar',
    remove: 'Remover',
    countOne: '{{count}} recurso',
    countOther: '{{count}} recursos',

    confirmTitle: 'Remover recurso',
    confirmWithAllocationsOne: 'Este recurso possui {{count}} alocação ativa. Remover o recurso irá excluir todas as alocações associadas.',
    confirmWithAllocationsOther: 'Este recurso possui {{count}} alocações ativas. Remover o recurso irá excluir todas as alocações associadas.',
    confirmPlain: 'Remover este recurso? As alocações associadas também serão removidas.',
    confirmLabel: 'Remover',
  },

  /** Simulador de projeto (wizard de 3 passos). */
  simulator: {
    title: 'Simular Novo Projeto',
    subtitle: 'Verifique se sua equipe tem capacidade para aceitar',
    steps: {
      info: 'Informações',
      demand: 'Demanda',
      team: 'Time',
    },
    back: 'Voltar',
    cancel: 'Cancelar',
    next: 'Próximo',
    creating: 'Criando...',
    create: 'Criar Projeto e Alocar',

    /**
     * Papéis-semente do passo 2. São campos de TEXTO LIVRE editáveis: o valor
     * digitado vira `role_in_project` e é casado por substring contra o cargo
     * dos recursos. Traduzir a semente é o que faz o casamento funcionar para
     * uma equipe que cadastrou os cargos no próprio idioma.
     */
    defaultRoles: ['Desenvolvedor', 'QA', 'Product Owner', 'UX Designer'],

    step1: {
      title: 'Informações do Projeto',
      subtitle: 'Defina os dados básicos do projeto que deseja simular',
      name: 'Nome do projeto',
      namePlaceholder: 'Ex: Redesign do e-commerce',
      startDate: 'Data de início',
      endDate: 'Data de go-live',
      datePlaceholder: 'Selecionar data',
      complexity: 'Complexidade',
      complexityLow: 'Baixa',
      complexityMedium: 'Média',
      complexityHigh: 'Alta',
      invalidRange: 'Data de go-live anterior ao início',
      /** `{{days}}` dias corridos, `{{weeks}}` semanas. */
      duration: '{{days}} dias ({{weeks}} semanas)',
    },

    step2: {
      title: 'Demanda Estimada',
      subtitle: 'Informe o esforço por papel para este projeto',
      templates: 'Templates:',
      colRole: 'Cargo / Papel',
      colHours: 'Horas Totais',
      colHoursPerWeek: 'Horas/Semana',
      rolePlaceholder: 'Ex: Desenvolvedor',
      remove: 'Remover',
      addRole: 'Adicionar papel',
      total: 'Total estimado:',
      totalHint: '({{perWeek}}h/semana em {{weeks}} semanas)',
      saveTemplate: 'Salvar como Template',
      templateNamePlaceholder: 'Nome do template',
      save: 'Salvar',
      cancel: 'Cancelar',
    },

    step3: {
      loading: 'Analisando disponibilidade...',
      title: 'Sugestão de Time',
      subtitle: 'Com base na disponibilidade dos recursos no período',
      verdictGreen: 'Viável',
      verdictYellow: 'Viável com ajustes',
      verdictRed: 'Inviável',
      descGreen: 'Todos os papéis podem ser preenchidos sem overbooking.',
      descYellow: 'Alguns papéis têm disponibilidade parcial — pode haver semanas com sobrecarga.',
      descRed: 'Um ou mais papéis não possuem recursos disponíveis no período.',
      hours: '{{total}}h total ({{perWeek}}h/sem)',
      noResource: 'Sem recurso disponível',
      candidatesOne: '{{count}} candidato',
      candidatesOther: '{{count}} candidatos',
      noCandidates: 'Nenhum recurso compatível encontrado para este papel',
      noRole: 'Sem cargo',
      available: 'Disp: {{hours}}h/sem',
      fit: 'Fit: {{pct}}%',
      overflow: 'Estoura em {{count}} sem.',
      noRoles: 'Nenhum papel com horas definido no passo anterior',
    },
  },

  /** Card de onboarding do módulo (3 passos). */
  onboarding: {
    /** `{{current}}` de `{{total}}`. */
    step: 'Passo {{current}} de {{total}}',
    skip: 'Pular',
    goToTeam: 'Ir para Equipe',
    next: 'Próximo',
    start: 'Começar',
    steps: [
      {
        title: 'Adicione sua equipe',
        description: 'Cadastre os membros da equipe nos seus projetos. Cada membro pode ter um cargo e capacidade semanal definida.',
        tip: 'Dica: membros da organização podem ser importados automaticamente.',
      },
      {
        title: 'As tarefas definem a carga',
        description: 'O MeuGantt calcula automaticamente a carga de trabalho de cada membro baseado na duração das tarefas atribuídas.',
        tip: 'Dica: preencha a duração das tarefas (ex: 5d, 2w) para cálculos mais precisos.',
      },
      {
        title: 'Aloque recursos para o futuro',
        description: 'Use a alocação manual para planejar capacidade futura — antes mesmo de criar tarefas. Veja quem tem disponibilidade para novos projetos.',
        tip: 'Dica: o simulador de projetos ajuda a testar cenários de alocação.',
      },
    ],
  },

  /**
   * Tour do dashboard EVM (react-joyride). Em `nextWithProgress` as chaves em
   * chave simples (`{current}`) são do próprio joyride, não do nosso `fill`.
   */
  evmTour: {
    steps: [
      {
        title: 'Dashboard EVM',
        content: 'Esses cards aplicam Earned Value Management (PMBoK 7ª ed.) — comparam o que foi planejado (PV) com o que foi entregue (EV) e o que custou (AC). Hover em cada sigla pra entender. SPI/CPI traz índices de desempenho.',
      },
      {
        title: 'Curva S — leitura visual',
        content: 'A Curva S mostra a evolução semanal: linha azul (PV) é o planejado, verde (EV) é o entregue, amarela (AC) é o real. A linha tracejada vermelha é a previsão de custo final (EAC). Em projeto saudável, as 3 sólidas ficam coladas.',
      },
      {
        title: 'Próximo passo',
        content: 'Quer entender em detalhes? Clique em "Saiba mais" no canto superior direito do card EVM e abre o artigo completo da Wiki — explicação PM-friendly + cenários típicos.',
      },
    ],
    locale: {
      back: 'Voltar',
      close: 'Fechar',
      last: 'Concluir',
      next: 'Próximo',
      nextWithProgress: '{current} de {total}',
      open: 'Abrir',
      skip: 'Pular tour',
    },
  },
};

/** en e es derivam desta forma: chave faltando vira erro de compilação. */
export type PortfolioCopy = typeof portfolio;

export default portfolio;
